import { computed } from 'vue'
import { i18n } from '@/plugins/i18n'
import { systemSkillDefinitions } from '@/config/ai/prompts'
import { dynamicSkillList } from './prompts'
import { normalizePromptIcon } from '@/config/ai/skillIcons'
import type {
  AiSkill,
  AiSkillDefinition,
  AiSkillOrigin,
  DocumentType
} from '@/types'
import { sortSkillsByOrder } from '@/lib/ai/skill'
import { conversations, userSkillDefinitions } from './state'
import type { TokenUsage } from './types'

const EMPTY_USAGE: TokenUsage = {
  inputTokens: 0,
  outputTokens: 0,
  totalTokens: 0,
  cost: 0
}

const locale = computed(() => i18n.global.locale.value)

function toUiSkillAction(
  skill: AiSkill | AiSkillDefinition,
  origin: AiSkillOrigin
): AiSkill {
  if ('buildContent' in skill) {
    return {
      id: skill.id,
      label: skill.label,
      labelKey: skill.labelKey,
      icon: normalizePromptIcon(skill.icon),
      type: skill.type,
      order: skill.order,
      documentTypes: skill.documentTypes,
      maxSteps: skill.maxSteps,
      origin,
      buildContent: skill.buildContent
    }
  }

  return {
    id: skill.id,
    label: skill.labels?.[i18n.global.locale.value] ?? skill.label ?? skill.id,
    icon: normalizePromptIcon(skill.icon),
    type: skill.type,
    order: skill.order,
    documentTypes: skill.documentTypes,
    maxSteps: skill.maxSteps,
    origin,
    buildContent: () => skill.content
  }
}

export const systemSkills = computed<AiSkill[]>(() => {
  void locale.value
  return systemSkillDefinitions.value.map(definition =>
    toUiSkillAction(definition, 'system')
  )
})

export const systemContextualSkills = computed<AiSkill[]>(() =>
  systemSkills.value.filter(skill => skill.type === 'contextual')
)

export const systemGeneralSkills = computed<AiSkill[]>(() =>
  systemSkills.value.filter(skill => skill.type === 'general')
)

export const dynamicSkills = computed<AiSkill[]>(() => {
  void locale.value
  return dynamicSkillList.map(skill =>
    Object.assign(toUiSkillAction(skill, 'dynamic'), {
      label:
        skill.label ?? (skill.labelKey ? i18n.global.t(skill.labelKey) : '')
    })
  )
})

export const userSkills = computed<AiSkill[]>(() =>
  userSkillDefinitions.value.map(definition =>
    toUiSkillAction(definition, 'user')
  )
)

export const userContextualSkills = computed<AiSkill[]>(() =>
  userSkills.value.filter(skill => skill.type === 'contextual')
)

export const userGeneralSkills = computed<AiSkill[]>(() =>
  userSkills.value.filter(skill => skill.type === 'general')
)

// Skills liés à une dépêche, accessibles depuis le menu IA d'un viewer.
// Les skills utilisateur sont placés APRÈS les prédéfinis (chaque groupe
// trié par `order`), pas mélangés par un tri global.
export const contextualSkills = computed<AiSkill[]>(() => [
  ...[...systemContextualSkills.value].toSorted(sortSkillsByOrder),
  ...[...userContextualSkills.value].toSorted(sortSkillsByOrder)
])

// Skills généralistes, affichés sur la page d'accueil du chat. Idem :
// prédéfinis (système + dynamiques) d'abord, skills utilisateur ensuite.
export const suggestions = computed<AiSkill[]>(() => [
  ...[...systemGeneralSkills.value, ...dynamicSkills.value].toSorted(
    sortSkillsByOrder
  ),
  ...[...userGeneralSkills.value].toSorted(sortSkillsByOrder)
])

export const availablePromptActions = computed<AiSkill[]>(() => [
  ...suggestions.value,
  ...contextualSkills.value
])

// Skills contextuels applicables à un type de document donné.
// Un skill sans `documentTypes` s'applique à tous les documents.
export function contextualSkillsForType(docType: DocumentType): AiSkill[] {
  return contextualSkills.value.filter(
    skill => !skill.documentTypes || skill.documentTypes.includes(docType)
  )
}

export function getTokenUsage(conversationId: string): TokenUsage {
  return (
    conversations.value.find(c => c.id === conversationId)?.token_usage ??
    EMPTY_USAGE
  )
}
